// src/components/weopen/WeOpenComposer.tsx
//
// Agent reply composer for a WeOpen conversation (Phase 4 PR 1).
//
// ── PR1 REPLIES; IT NEVER ACQUIRES OWNERSHIP ────────────────────────────────
//
// WeOpen's send endpoint has one behaviour PR1 must NOT reach: sending to a
// BOT-status conversation is an agent-initiated JOIN — WeOpen flips it to `human`
// and assigns it to the sender (`agentInitiatedJoin: true`). That is ownership
// acquisition, which belongs to Phase 4 PR2's explicit claim. So this composer
// refuses to DISPATCH for any conversation whose ownership is not already
// established, and an unclaimed conversation is never sent to at all.
//
// ── THAT REFUSAL IS NOT A SECOND AUTHORITY ──────────────────────────────────
//
// The gate is strictly NARROWING and one-directional: it can only ever decline to
// send, never permit something WeOpen would refuse. Note what it does NOT do — it
// never asks "is this assigned to ME". It has no notion of the current user at
// all, only whether an owner EXISTS. So a conversation owned by another agent is
// still dispatched and still answered by WeOpen with 409
// `CONVERSATION_ALREADY_ASSIGNED`; CRM does not pre-empt that decision.
//
// ── IDEMPOTENCY: ONE KEY PER LOGICAL MESSAGE ────────────────────────────────
//
// The key is minted for a (conversation, content) pair and HELD until that
// message is confirmed sent:
//
//   retry the same text        → SAME key → WeOpen replays, customer sees one copy
//   edit the text, then send   → NEW key  → a different message, correctly
//   after a confirmed success  → NEW key  → the next message is its own thing
//
// The edit case matters: WeOpen fingerprints the body, so reusing a key with
// different content is 409 `IDEMPOTENCY_KEY_REUSED`. Tying the key to the content
// makes that unreachable by construction rather than by remembering to be careful.
//
// ── THE TIMEOUT IS THE DANGEROUS CASE ───────────────────────────────────────
//
// A dispatched send that never answered MAY HAVE DELIVERED. The BFF flags it
// `outcomeUnknown`, and this component then says so plainly and keeps the key, so
// pressing Retry cannot produce a second copy. What it must never do is treat
// that as a clean failure, clear the box, and let the agent retype — that is the
// path to a customer reading the same reply twice.
//
// ── NO OPTIMISTIC MESSAGE ───────────────────────────────────────────────────
//
// The authoritative message is WeOpen's. On success the parent refetches the
// transcript. An optimistic row would need reconciliation the Phase 3 read
// architecture has no mechanism for, and a ghost or duplicate in a customer
// conversation is worse than a moment's wait. Realtime is Phase 4 PR 4.

import { useRef, useState } from 'react';
import {
  useSendWeOpenAgentMessageMutation,
  type WeOpenConversationDetail,
} from '../../api/weopenReadApi';

/** A key WeOpen accepts: 8–255 chars of [A-Za-z0-9._:~-]. */
function mintIdempotencyKey(): string {
  const rand =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  return `crm-send-${rand}`;
}

export type SendOutcome =
  | { kind: 'idle' }
  | { kind: 'sending' }
  /** Confirmed rejection. Retrying with the same key is safe and re-executes. */
  | { kind: 'rejected'; status?: number; code?: string; message: string }
  /** Dispatched, no answer. MAY have sent. Retry only with the same key. */
  | { kind: 'unknown'; message: string };

/**
 * The outcomes that carry a message to show. `describeSendError` can only ever
 * produce these two, and saying so in the type means a caller never has to guard
 * for an `idle`/`sending` result that cannot occur.
 */
export type SendFailure = Extract<SendOutcome, { kind: 'rejected' } | { kind: 'unknown' }>;

/** Turn a BFF error into something an agent can act on. */
export function describeSendError(err: unknown): SendFailure {
  const status = (err as { status?: number } | undefined)?.status;
  const data = (err as { data?: { error?: string; message?: string; outcomeUnknown?: boolean } })
    ?.data;
  const code = data?.error;

  if (data?.outcomeUnknown || status === 504 || status === 502) {
    return {
      kind: 'unknown',
      message:
        'We could not confirm whether this reply was delivered. Retry — it will not send twice.',
    };
  }

  const known: Record<string, string> = {
    CONVERSATION_NOT_CLAIMED: 'Claim this conversation in WeOpen before replying.',
    CONVERSATION_ALREADY_ASSIGNED: 'Another agent is handling this conversation.',
    IDEMPOTENCY_KEY_REUSED: 'That message was already sent with different content.',
    IDEMPOTENCY_REQUEST_IN_PROGRESS: 'This reply is still being processed. Give it a moment.',
    IDEMPOTENCY_UNAVAILABLE: 'WeOpen could not verify this send right now. Retry shortly.',
    AUDIENCE_UNAVAILABLE: 'WeOpen could not verify your access right now. Retry shortly.',
  };
  if (code && known[code]) return { kind: 'rejected', status, code, message: known[code] };

  if (status === 400) {
    return {
      kind: 'rejected',
      status,
      code,
      message: data?.message || 'This conversation cannot accept a reply.',
    };
  }
  if (status === 403) {
    return { kind: 'rejected', status, code, message: 'You do not have access to this conversation.' };
  }
  if (status === 404) {
    // Non-revealing on purpose: missing and out-of-scope are the same answer.
    return { kind: 'rejected', status, code, message: 'This conversation is unavailable.' };
  }
  return { kind: 'rejected', status, code, message: 'The reply could not be sent.' };
}

export interface WeOpenComposerProps {
  conversationId: string;
  /** Detail, for presentation only — never as the authority on sending. */
  conversation: WeOpenConversationDetail | null | undefined;
  /** Called after a confirmed send so the parent can refetch the transcript. */
  onSent: () => void;
}

export default function WeOpenComposer({
  conversationId,
  conversation,
  onSent,
}: WeOpenComposerProps) {
  const [text, setText] = useState('');
  const [outcome, setOutcome] = useState<SendOutcome>({ kind: 'idle' });
  const [send, { isLoading }] = useSendWeOpenAgentMessageMutation();

  /** The in-flight logical message: its key and the exact content it covers. */
  const pending = useRef<{ key: string; content: string } | null>(null);

  const trimmed = text.trim();

  const status = conversation?.status;
  const closed = status === 'closed';

  /**
   * Ownership must ALREADY exist for PR1 to send.
   *
   *   bot                          → sending would JOIN and assign. Refused: PR2.
   *   human + assignedAgentId null → unclaimed. WeOpen answers 409
   *                                  CONVERSATION_NOT_CLAIMED, and claiming is PR2,
   *                                  so there is nothing to gain by dispatching.
   *   human + an owner exists      → dispatch. If the owner is someone else,
   *                                  WeOpen says so — CRM does not guess.
   *
   * Deliberately no comparison against the current user: "an owner exists" is all
   * this needs, so CRM never holds an opinion about WHO owns it.
   */
  const needsClaimFirst = status === 'bot' || (status === 'human' && !conversation?.assignedAgentId);

  const canAttempt = trimmed.length > 0 && !isLoading && !closed && !needsClaimFirst;

  async function handleSend() {
    // `canAttempt` already covers closed and unclaimed; re-checked here so the
    // refusal cannot be bypassed by a stray programmatic call.
    if (!canAttempt || closed || needsClaimFirst) return;

    // Same content as the outstanding attempt → same key. Different content (or a
    // fresh message) → new key.
    if (!pending.current || pending.current.content !== trimmed) {
      pending.current = { key: mintIdempotencyKey(), content: trimmed };
    }

    setOutcome({ kind: 'sending' });
    try {
      await send({
        conversationId,
        content: trimmed,
        idempotencyKey: pending.current.key,
      }).unwrap();

      // Confirmed. Release the key so the next message is its own logical send.
      pending.current = null;
      setText('');
      setOutcome({ kind: 'idle' });
      onSent();
    } catch (err) {
      // The key is deliberately RETAINED here, for both rejection and ambiguity.
      setOutcome(describeSendError(err));
    }
  }

  return (
    <div data-testid="weopen-composer" className="border-t border-gray-200 p-3">
      {closed && (
        <p data-testid="weopen-composer-closed" className="mb-2 text-xs text-gray-500">
          This conversation is closed.
        </p>
      )}

      {needsClaimFirst && !closed && (
        // Covers BOT and unclaimed HUMAN alike. Purely informational — no request
        // is made, and nothing here changes assignment or status.
        <p data-testid="weopen-composer-needs-claim" className="mb-2 text-xs text-amber-700">
          Claim this conversation before replying.
        </p>
      )}

      <textarea
        data-testid="weopen-composer-input"
        value={text}
        onChange={(e) => setText(e.target.value)}
        disabled={closed || needsClaimFirst || isLoading}
        rows={3}
        placeholder="Write a reply…"
        className="w-full resize-none rounded-md border border-gray-300 p-2 text-sm focus:border-blue-500 focus:outline-none disabled:bg-gray-50"
      />

      <div className="mt-2 flex items-center justify-between gap-3">
        <div className="min-h-[1rem] flex-1">
          {outcome.kind === 'rejected' && (
            <p data-testid="weopen-composer-error" className="text-xs text-red-600">
              {outcome.message}
            </p>
          )}
          {outcome.kind === 'unknown' && (
            <p data-testid="weopen-composer-unknown" className="text-xs text-amber-700">
              {outcome.message}
            </p>
          )}
        </div>

        <button
          type="button"
          data-testid="weopen-composer-send"
          onClick={() => void handleSend()}
          // `isLoading` is what stops a double click becoming two logical sends;
          // even if one slipped through, the retained key makes it a replay.
          disabled={!canAttempt}
          className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300"
        >
          {isLoading ? 'Sending…' : outcome.kind === 'idle' ? 'Send' : 'Retry'}
        </button>
      </div>
    </div>
  );
}
